import { useCallback, useEffect, useState } from 'react';

import { useLocalStorage } from 'react-use';

import { useHiddenBodyOverflow } from '../../../hooks/useHiddenBodyOverflow';
import { analytics } from '../../../services/analytics';
import { postRequest } from '../../../services/api';
import type { AnimationGuideProgress } from '../../../services/types';
import { useStores } from '../../../stores/useStores';
import { ONBOARDING_CONFIG } from '../constants';

import { ONBOARDING_STEP_STORAGE_KEY } from './helper';
import type { RiveEventProperties, BaseRiveEventProperties } from './types';

export function useInteractiveAnimationFTUE() {
  const { onboardingStore, userStore, walletStore } = useStores();

  const isOnboardingActive = onboardingStore.currentFtueType === 'interactiveAnimation';

  const [showOnboarding, setShowOnboarding] = useState(false);
  const [savedStep, setSavedStep, clearSavedStep] = useLocalStorage<AnimationGuideProgress | null>(
    ONBOARDING_STEP_STORAGE_KEY(userStore.profile?.firebaseUid || ''),
    null,
  );

  useHiddenBodyOverflow(showOnboarding);

  const sendPropertiesAnalytics = useCallback(
    (properties: BaseRiveEventProperties, fileUrl: string, skipStepNumber: number, ftueType?: string) => {
      void analytics.sendEvent(properties.event_name, {
        event_category: 'tutorial',
        event_action: properties.event_action || 'impression',
        step_name: properties.step_name,
        is_skip: properties.isSkip,
        step_number: properties.stageNum,
        url: fileUrl,
        ftue_type: ftueType,
        skip_step_number: skipStepNumber,
        ...(properties.step_name === 'step_1_tutorial' ? { triggered_by: 'sign_up' } : {}),
      });
    },
    [],
  );

  const closeFTUE = useCallback(() => {
    onboardingStore.finishFtue();
    setShowOnboarding(false);
    clearSavedStep();
  }, [onboardingStore, clearSavedStep]);

  const onLastStepVisible = useCallback(async () => {
    await postRequest('user/ftue-tutorial/finish');
    await walletStore.fetchWallet();
    void analytics.sendEvent('onboarding_finished', {
      event_category: 'tutorial',
    });
  }, [walletStore]);

  const finishOnBoardingTourGuide = useCallback(() => {
    userStore.updateProfile({ isOnboardingCompleted: true });
    closeFTUE();
  }, [closeFTUE, userStore]);

  const skipOnboardingTourGuide = useCallback(async () => {
    await postRequest('user/ftue-tutorial/finish');
    await walletStore.fetchWallet();
    void analytics.sendEvent('tutorial_skipped', {
      event_category: 'tutorial',
      triggered_by: 'click',
      event_action: 'click',
      step_name: savedStep?.stepName || '',
      stepNum: savedStep?.stageNum || 0,
    });
    finishOnBoardingTourGuide();
  }, [finishOnBoardingTourGuide, savedStep, walletStore]);

  const terminateOnboardingTourGuideFlow = useCallback(
    (reason: string, animationType: 'rive') => {
      void analytics.sendEvent('onboarding_terminated', {
        reason,
        event_category: 'tutorial',
        type: 'interactive_animation',
        animationType,
      });
      finishOnBoardingTourGuide();
    },
    [finishOnBoardingTourGuide],
  );

  const saveStepToLocalStorage = useCallback(
    (properties: BaseRiveEventProperties, fileUrl: string) => {
      if (!('stageNum' in properties)) {
        return;
      }
      const currentStageNum = properties.stageNum || 0;
      if (savedStep && savedStep.stageNum >= currentStageNum) {
        return;
      }
      setSavedStep({
        stepName: properties.step_name,
        riveFileName: fileUrl,
        firebaseUserId: userStore.profile?.firebaseUid || '',
        stageNum: currentStageNum,
        eventName: properties.event_name,
      });
    },
    [savedStep, userStore.profile?.firebaseUid, setSavedStep],
  );

  const checkIfStageNumberExpired = useCallback(
    (stepNum: number): boolean => {
      if (!savedStep) {
        return false;
      }
      return stepNum <= savedStep.stageNum;
    },
    [savedStep],
  );

  const stepNumberHandler = useCallback(
    (stepNum: number) => {
      if (checkIfStageNumberExpired(stepNum)) {
        return { isStepAlreadyViewed: true };
      }
      return { isStepAlreadyViewed: false };
    },
    [checkIfStageNumberExpired],
  );

  const onGuideEvent = useCallback(
    (
      properties: RiveEventProperties,
      fileUrl: string,
      skipTriggerInput?: { fire: () => void },
      wasInitialSkipTriggered?: boolean,
      skipStepNumber?: number,
      ftueType?: string,
    ) => {
      try {
        if ('stageNum' in properties && properties.stageNum) {
          const { isStepAlreadyViewed } = stepNumberHandler(properties.stageNum);
          if (isStepAlreadyViewed) {
            return;
          }
        }
        if ('isFinish' in properties && properties.isFinish) {
          finishOnBoardingTourGuide();
          return;
        }
        if ('isSkip' in properties && properties.isSkip) {
          void skipOnboardingTourGuide();
          return;
        }
        if ('isLastStep' in properties && properties.isLastStep) {
          if (wasInitialSkipTriggered) {
            skipTriggerInput?.fire();
          }
          void onLastStepVisible();
        }
        if ('event_name' in properties && 'step_name' in properties) {
          saveStepToLocalStorage(properties, fileUrl);
          sendPropertiesAnalytics(properties, fileUrl, skipStepNumber || 0, ftueType);
        }
      } catch (error) {
        terminateOnboardingTourGuideFlow(`error_handling_event: ${(error as Error)?.message}`, 'rive');
      }
    },
    [
      finishOnBoardingTourGuide,
      sendPropertiesAnalytics,
      onLastStepVisible,
      terminateOnboardingTourGuideFlow,
      skipOnboardingTourGuide,
      saveStepToLocalStorage,
      stepNumberHandler,
    ],
  );

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (isOnboardingActive) {
      timeoutId = setTimeout(() => setShowOnboarding(true), ONBOARDING_CONFIG.ANIMATION_DELAY);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isOnboardingActive]);

  return {
    showOnboarding: showOnboarding && isOnboardingActive,
    terminateOnboardingTourGuideFlow,
    onGuideEvent,
    savedStep,
  };
}
